<template>
  <v-dialog
    v-model="dialogModel"
    max-width="1180"
    width="calc(100% - 32px)"
    scrollable
    data-testid="mobile-ui-settings-dialog"
  >
    <v-card class="mobile-settings-card">
      <v-card-title class="mobile-settings-header">
        <div class="mobile-settings-heading">
          <div class="mobile-settings-icon">
            <v-icon size="24">mdi-cellphone-cog</v-icon>
          </div>
          <div>
            <div class="mobile-settings-title">Mobile product display settings</div>
            <div class="mobile-settings-subtitle">Choose which products appear on the mobile recommendation screen</div>
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" aria-label="Close mobile product settings" @click="closeDialog" />
      </v-card-title>

      <v-tabs v-model="activeCategory" color="primary" class="mobile-settings-tabs" show-arrows>
        <v-tab :value="prefixTab" data-testid="mobile-ui-prefix-tab">PREFIX</v-tab>
        <v-tab :value="categoriesTab" data-testid="mobile-ui-categories-tab">CATEGORIES</v-tab>
        <v-tab v-for="category in categories" :key="category" :value="category">{{ category }}</v-tab>
      </v-tabs>

      <v-divider />

      <v-progress-linear v-if="loading" indeterminate color="primary" height="3" />

      <v-card-text class="mobile-settings-body">
        <v-alert
          v-if="loadError || saveError || validationMessage"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-5"
          data-testid="mobile-ui-settings-error"
        >
          {{ loadError || saveError || validationMessage }}
        </v-alert>
        <v-alert
          v-if="saveSuccess"
          type="success"
          variant="tonal"
          density="compact"
          class="mb-5"
          data-testid="mobile-ui-settings-success"
        >
          {{ saveSuccess }}
        </v-alert>

        <div v-if="loading && !categoryList.length" class="category-placeholder">
          <v-progress-circular indeterminate color="primary" size="32" />
          <div class="section-description">Loading product display settings…</div>
        </div>

        <v-window v-else v-model="activeCategory">
          <v-window-item :value="prefixTab">
            <section class="category-manager prefix-manager" aria-labelledby="prefix-manager-title">
              <div class="section-heading-row">
                <div>
                  <div id="prefix-manager-title" class="section-title">Prefix</div>
                  <div class="section-description">
                    Screen-level prefixes the mobile app prints in front of products and funds. Edit, then press SAVE once.
                    Leave a box empty to print nothing. Spaces are kept as typed — end with a space to separate the prefix from the title.
                  </div>
                </div>
              </div>

              <!-- Same card as the fund groups on the category tabs, so the tabs read as one dialog. -->
              <div class="fund-group-list prefix-card-list">
                <div
                  v-for="(group, groupIndex) in prefixGroups"
                  :key="group.key"
                  class="fund-group-card prefix-card"
                  :data-testid="`mobile-ui-prefix-group-${group.key}`"
                >
                  <div class="fund-group-card-head">
                    <v-chip color="primary" variant="tonal" size="small">{{ groupIndex + 1 }}</v-chip>
                    <span class="fund-group-card-name">{{ group.title }}</span>
                    <span class="prefix-card-caption">{{ group.caption }}</span>
                  </div>

                  <div v-for="field in group.fields" :key="field.key" class="prefix-field">
                    <label :for="`prefix-field-${field.key}`" class="prefix-field-label">{{ field.label }}</label>
                    <v-text-field
                      :id="`prefix-field-${field.key}`"
                      :model-value="prefixValues ? prefixValues[field.key] : ''"
                      :disabled="!prefixValues"
                      :error="prefixTooLongKeys.includes(field.key)"
                      variant="outlined"
                      density="comfortable"
                      rounded="pill"
                      hide-details
                      :placeholder="field.placeholder"
                      :data-testid="`mobile-ui-prefix-${field.key}`"
                      @update:model-value="setPrefixValue(field.key, $event)"
                    />
                    <div class="prefix-field-hint">{{ field.key }}</div>
                  </div>
                </div>
              </div>
            </section>
          </v-window-item>

          <v-window-item :value="categoriesTab">
            <section class="category-manager" aria-labelledby="category-manager-title">
              <div class="section-heading-row">
                <div>
                  <div id="category-manager-title" class="section-title">Product categories</div>
                  <div class="section-description">
                    Drag or use the arrows to reorder, edit the labels shown on mobile, pick the default category, then press SAVE once.
                  </div>
                </div>
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="small"
                  prepend-icon="mdi-plus"
                  data-testid="mobile-ui-category-add"
                  @click="addCategory"
                >
                  ADD CATEGORY
                </v-btn>
              </div>

              <div class="category-table-wrap">
                <v-data-table
                  :headers="categoryHeaders"
                  :items="categoryList"
                  :loading="loading"
                  class="category-data-table"
                  density="comfortable"
                  :items-per-page="-1"
                  fixed-header
                  hide-default-footer
                  hide-default-body
                >
                  <template v-slot:colgroup>
                    <col
                      v-for="header in categoryHeaders"
                      :key="header.key"
                      :style="{ width: header.width, minWidth: header.width }"
                    />
                  </template>

                  <template v-slot:tbody>
                    <draggable
                      v-if="categoryList.length"
                      v-model="draggableCategories"
                      item-key="rowKey"
                      tag="tbody"
                      class="v-data-table__tbody category-draggable-body"
                      handle=".drag-handle"
                      ghost-class="drag-ghost"
                      chosen-class="drag-chosen"
                      drag-class="drag-active"
                      :animation="180"
                      :disabled="dragDisabled"
                    >
                      <template #item="{ element, index }">
                        <tr
                          class="v-data-table__tr category-row"
                          :class="{ 'category-row--off': !element.isActive }"
                          :data-testid="`mobile-ui-category-row-${categoryRowId(element, index)}`"
                        >
                          <td class="v-data-table__td text-center order-column">
                            <div class="order-cell">
                              <v-btn
                                variant="text"
                                density="compact"
                                icon
                                class="drag-handle"
                                :disabled="dragDisabled"
                                aria-label="Drag to reorder"
                              >
                                <v-icon size="18">mdi-drag-vertical</v-icon>
                                <v-tooltip activator="parent" location="top">Drag to reorder</v-tooltip>
                              </v-btn>
                              <v-chip color="primary" variant="tonal" size="small">
                                {{ element.sortOrder || index + 1 }}
                              </v-chip>
                            </div>
                          </td>

                          <td class="v-data-table__td text-center">
                            <div class="move-cell">
                              <v-btn
                                variant="text"
                                density="compact"
                                icon
                                :disabled="index === 0"
                                :data-testid="`mobile-ui-category-move-up-${index}`"
                                aria-label="Move category up"
                                @click="moveCategory(index, -1)"
                              >
                                <v-icon size="18">mdi-arrow-up</v-icon>
                                <v-tooltip activator="parent" location="top">Move Up</v-tooltip>
                              </v-btn>
                              <v-btn
                                variant="text"
                                density="compact"
                                icon
                                :disabled="index === categoryList.length - 1"
                                :data-testid="`mobile-ui-category-move-down-${index}`"
                                aria-label="Move category down"
                                @click="moveCategory(index, 1)"
                              >
                                <v-icon size="18">mdi-arrow-down</v-icon>
                                <v-tooltip activator="parent" location="top">Move Down</v-tooltip>
                              </v-btn>
                            </div>
                          </td>

                          <td class="v-data-table__td">
                            <v-text-field
                              :model-value="element.categoryCode"
                              :disabled="element.isPersisted"
                              :error="invalidCodeRowKeys.includes(element.rowKey)"
                              variant="outlined"
                              density="compact"
                              hide-details
                              placeholder="NEW_CATEGORY"
                              :data-testid="`mobile-ui-category-code-${categoryRowId(element, index)}`"
                              @update:model-value="setCategoryCode(element, $event)"
                            />
                          </td>

                          <td class="v-data-table__td">
                            <v-text-field
                              v-model="element.labelTH"
                              variant="outlined"
                              density="compact"
                              hide-details
                              placeholder="ประกันชีวิต/สุขภาพ"
                              :data-testid="`mobile-ui-category-labelth-${categoryRowId(element, index)}`"
                            />
                          </td>
                          <td class="v-data-table__td">
                            <v-text-field
                              v-model="element.labelEN"
                              variant="outlined"
                              density="compact"
                              hide-details
                              placeholder="Life/Health Insurance"
                              :data-testid="`mobile-ui-category-labelen-${categoryRowId(element, index)}`"
                            />
                          </td>
                          <td class="v-data-table__td">
                            <v-text-field
                              v-model="element.labelCN"
                              variant="outlined"
                              density="compact"
                              hide-details
                              :data-testid="`mobile-ui-category-labelcn-${categoryRowId(element, index)}`"
                            />
                          </td>

                          <td class="v-data-table__td text-center">
                            <div class="d-flex justify-center">
                              <v-switch
                                v-model="element.isActive"
                                color="success"
                                density="compact"
                                hide-details
                                inset
                                :data-testid="`mobile-ui-category-active-${categoryRowId(element, index)}`"
                              />
                            </div>
                          </td>

                          <td class="v-data-table__td text-center">
                            <div class="d-flex justify-center">
                              <!-- @click, never @update:model-value — see setDefaultCategory. -->
                              <v-radio
                                :model-value="element.isDefault ? categoryRowId(element, index) : null"
                                :value="categoryRowId(element, index)"
                                color="#4E3982"
                                density="compact"
                                hide-details
                                :data-testid="`mobile-ui-category-default-${categoryRowId(element, index)}`"
                                @click="setDefaultCategory(element)"
                              />
                            </div>
                          </td>

                          <td class="v-data-table__td text-center">
                            <v-btn
                              variant="text"
                              density="comfortable"
                              icon
                              color="error"
                              :data-testid="`mobile-ui-category-delete-${categoryRowId(element, index)}`"
                              aria-label="Delete category"
                              @click="removeCategory(index, element.labelTH || element.categoryCode)"
                            >
                              <v-icon size="18">mdi-delete-outline</v-icon>
                              <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </draggable>

                    <tbody v-else class="v-data-table__tbody">
                      <tr class="v-data-table__tr">
                        <td class="v-data-table__td category-empty-cell" :colspan="categoryHeaders.length">
                          No categories yet — press ADD CATEGORY to create the first one.
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-data-table>
              </div>
            </section>
          </v-window-item>

          <v-window-item v-for="category in categories" :key="category" :value="category">
            <div class="category-settings-layout">
              <section class="subtype-catalog" :aria-labelledby="`subtype-catalog-title-${category}`">
                <div class="section-heading-row">
                  <div>
                    <div :id="`subtype-catalog-title-${category}`" class="section-title">
                      {{ categoryTitleFor(category) }}
                    </div>
                    <div class="section-description">Select the subcategories to display on mobile.</div>
                  </div>
                  <v-chip
                    :data-testid="`mobile-ui-selected-count-${category}`"
                    color="primary"
                    variant="tonal"
                    size="small"
                  >
                    {{ selectedCountFor(category) }} selected
                  </v-chip>
                </div>

                <div
                  class="subtype-table-wrap"
                  :class="{ 'subtype-table-wrap--empty': !subcategoriesFor(category).length }"
                >
                  <table class="subtype-table">
                    <thead>
                      <tr>
                        <th scope="col">Product Type</th>
                        <th scope="col">Subcategory</th>
                        <th scope="col" class="subtype-table-action-heading">Show on mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!subcategoriesFor(category).length" class="subtype-row--empty">
                        <td colspan="3">No active subcategories are available.</td>
                      </tr>
                      <tr
                        v-for="subcategory in subcategoriesFor(category)"
                        :key="subcategory.code"
                        :data-testid="`mobile-ui-subtype-row-${subcategory.code}`"
                        :class="{ 'subtype-row--active': selectedSubtypeCodeByCategory[category] === subcategory.code }"
                        tabindex="0"
                        :aria-selected="selectedSubtypeCodeByCategory[category] === subcategory.code"
                        @click="selectSubtype(category, subcategory.code)"
                        @keydown.enter="selectSubtype(category, subcategory.code)"
                      >
                        <td><span class="subtype-type">{{ subcategory.productType || '—' }}</span></td>
                        <td>
                          <span class="subtype-name">{{ subcategory.name }}</span>
                          <!-- The code the row is published under, so a merged pair reads as one at a glance. -->
                          <span
                            v-if="isSubtypeSelected(category, subcategory.code) && subcategory.groupCode"
                            class="subtype-code"
                            :data-testid="`mobile-ui-subtype-code-${category}-${subcategory.code}`"
                          >
                            <v-icon v-if="groupMatesFor(category, subcategory).length" size="12" class="subtype-code-icon">mdi-link-variant</v-icon>{{ subcategory.groupCode }}
                          </span>
                        </td>
                        <td class="subtype-table-action-cell">
                          <label
                            class="subtype-toggle"
                            :for="`mobile-ui-toggle-${category}-${subcategory.code}`"
                            :data-testid="`mobile-ui-toggle-control-${category}-${subcategory.code}`"
                            @click.stop
                          >
                            <span class="sr-only">Show {{ subcategory.name }} on mobile</span>
                            <input
                              :id="`mobile-ui-toggle-${category}-${subcategory.code}`"
                              :data-testid="`mobile-ui-toggle-${category}-${subcategory.code}`"
                              type="checkbox"
                              :checked="isSubtypeSelected(category, subcategory.code)"
                              @change="setSubtypeEnabled(category, subcategory.code, $event.target.checked)"
                            />
                            <span class="subtype-toggle-track" aria-hidden="true"><span class="subtype-toggle-thumb" /></span>
                          </label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="subtype-editor" :aria-labelledby="`subtype-editor-title-${category}`">
                <div :id="`subtype-editor-title-${category}`" class="section-title">Mobile labels</div>
                <div class="section-description mb-5">
                  Select a row on the left to edit the labels shown in each supported language.
                </div>

                <div v-if="selectedSubtypeFor(category)" class="label-fields">
                  <v-alert
                    v-if="groupMatesFor(category, selectedSubtypeFor(category)).length"
                    type="info"
                    variant="tonal"
                    density="compact"
                    icon="mdi-link-variant"
                    :data-testid="`mobile-ui-group-merge-${category}`"
                  >
                    รวมเป็นกลุ่มเดียวกับ <strong>{{ groupMateNames(category, selectedSubtypeFor(category)) }}</strong> —
                    บนมือถือเห็นหัวข้อเดียว ปุ่มกรองเดียว และสินค้าของทุก subcategory ในกลุ่มต่อกัน
                    label 3 ช่องนี้ใช้ร่วมกันทั้งกลุ่ม แก้ที่แถวไหนก็เปลี่ยนทุกแถว
                  </v-alert>
                  <v-text-field
                    :model-value="selectedSubtypeFor(category).labelEN"
                    label="labelEN"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-translate"
                    hide-details
                    :data-testid="`mobile-ui-label-${category}-en`"
                    @update:model-value="updateLabel(category, 'labelEN', $event)"
                  />
                  <v-text-field
                    :model-value="selectedSubtypeFor(category).labelTH"
                    label="labelTH"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-translate"
                    hide-details
                    :data-testid="`mobile-ui-label-${category}-th`"
                    @update:model-value="updateLabel(category, 'labelTH', $event)"
                  />
                  <v-text-field
                    :model-value="selectedSubtypeFor(category).labelCN"
                    label="labelCN"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-translate"
                    hide-details
                    :data-testid="`mobile-ui-label-${category}-cn`"
                    @update:model-value="updateLabel(category, 'labelCN', $event)"
                  />
                  <v-text-field
                    :model-value="selectedSubtypeFor(category).groupCode"
                    label="groupCode"
                    placeholder="LIFE_INSURANCE_SELF"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-link-variant"
                    persistent-hint
                    persistent-placeholder
                    hint="รหัสที่ใช้จับคู่กับแถบจำลองในการ์ด Tax Simulation (A-Z 0-9 _) — ปล่อยว่าง = ใช้ SUBTYPE_{id} เหมือนเดิม ซึ่งจับคู่ไม่ได้ · ใส่รหัสเดียวกับ subcategory อื่นในแท็บนี้ = รวมเป็นกลุ่มเดียวกันบนมือถือ"
                    :data-testid="`mobile-ui-group-code-${category}`"
                    @update:model-value="updateGroupCode(category, $event)"
                  />
                </div>

                <v-alert v-else type="info" variant="tonal" density="compact" icon="mdi-information-outline">
                  Select at least one subcategory to edit its labels.
                </v-alert>

              </section>
            </div>

            <section class="fund-group-manager" :aria-labelledby="`fund-group-title-${category}`">
              <div class="section-heading-row">
                <div>
                  <div :id="`fund-group-title-${category}`" class="section-title">Fund groups</div>
                  <div class="section-description">
                    กลุ่มกองทุนที่จะแสดงในหมวดนี้ — พิมพ์ประเภทกองทุนให้ตรงกับข้อมูลต้นทาง ระบบเทียบแบบตรงตัว
                  </div>
                </div>
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="small"
                  prepend-icon="mdi-plus"
                  :data-testid="`mobile-ui-fundgroup-add-${category}`"
                  @click="addFundGroup(category)"
                >
                  ADD FUND GROUP
                </v-btn>
              </div>

              <div v-if="!fundGroupsFor(category).length" class="fund-group-empty">
                No fund groups yet — press ADD FUND GROUP to create the first one.
              </div>

              <div v-else class="fund-group-list">
                <div
                  v-for="(group, groupIndex) in fundGroupsFor(category)"
                  :key="group.rowKey"
                  class="fund-group-card"
                  :class="{ 'fund-group-card--off': !group.isActive }"
                  :data-testid="`mobile-ui-fundgroup-row-${category}-${groupIndex}`"
                >
                  <div class="fund-group-card-head">
                    <v-chip color="primary" variant="tonal" size="small">{{ groupIndex + 1 }}</v-chip>
                    <span class="fund-group-card-name">{{ fundGroupTitle(group, groupIndex) }}</span>

                    <div class="fund-group-card-actions">
                      <v-switch
                        v-model="group.isActive"
                        color="success"
                        density="compact"
                        hide-details
                        inset
                        label="Active"
                        class="fund-group-active"
                        :data-testid="`mobile-ui-fundgroup-active-${category}-${groupIndex}`"
                      />
                      <v-btn
                        variant="text"
                        density="compact"
                        icon
                        :disabled="groupIndex === 0"
                        :data-testid="`mobile-ui-fundgroup-up-${category}-${groupIndex}`"
                        aria-label="Move fund group up"
                        @click="moveFundGroup(category, groupIndex, -1)"
                      >
                        <v-icon size="18">mdi-arrow-up</v-icon>
                        <v-tooltip activator="parent" location="top">Move Up</v-tooltip>
                      </v-btn>
                      <v-btn
                        variant="text"
                        density="compact"
                        icon
                        :disabled="groupIndex === fundGroupsFor(category).length - 1"
                        :data-testid="`mobile-ui-fundgroup-down-${category}-${groupIndex}`"
                        aria-label="Move fund group down"
                        @click="moveFundGroup(category, groupIndex, 1)"
                      >
                        <v-icon size="18">mdi-arrow-down</v-icon>
                        <v-tooltip activator="parent" location="top">Move Down</v-tooltip>
                      </v-btn>
                      <v-btn
                        variant="text"
                        density="compact"
                        icon
                        color="error"
                        :data-testid="`mobile-ui-fundgroup-delete-${category}-${groupIndex}`"
                        aria-label="Delete fund group"
                        @click="removeFundGroup(category, groupIndex, fundGroupTitle(group, groupIndex))"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                      </v-btn>
                    </div>
                  </div>

                  <div class="fund-group-label-fields">
                    <v-text-field
                      v-model="group.labelTH"
                      label="Label TH"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-label-${category}-${groupIndex}-th`"
                    />
                    <v-text-field
                      v-model="group.labelEN"
                      label="Label EN"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-label-${category}-${groupIndex}-en`"
                    />
                    <v-text-field
                      v-model="group.labelCN"
                      label="Label CN"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-label-${category}-${groupIndex}-cn`"
                    />
                  </div>
                  <div class="fund-group-hint">
                    ปล่อย Label ว่างทั้ง 3 ภาษา = กลุ่มนี้จะไม่มีหัวข้อและไม่มีปุ่มกรองบนมือถือ
                  </div>

                  <div class="fund-group-label-fields">
                    <v-text-field
                      v-model="group.past1YearReturnLabelTH"
                      label="Past 1Y Return TH"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-past1yreturn-${category}-${groupIndex}-th`"
                    />
                    <v-text-field
                      v-model="group.past1YearReturnLabelEN"
                      label="Past 1Y Return EN"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-past1yreturn-${category}-${groupIndex}-en`"
                    />
                    <v-text-field
                      v-model="group.past1YearReturnLabelCN"
                      label="Past 1Y Return CN"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :data-testid="`mobile-ui-fundgroup-past1yreturn-${category}-${groupIndex}-cn`"
                    />
                  </div>
                  <div class="fund-group-hint">
                    ข้อความกำกับผลตอบแทนย้อนหลัง 1 ปี ที่จะแสดงบนมือถือ — ปล่อยว่าง = ไม่แสดงข้อความนี้
                  </div>

                  <v-combobox
                    :model-value="group.values"
                    label="Fund types"
                    placeholder="พิมพ์ประเภทกองทุนแล้วกด Enter"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    hide-no-data
                    multiple
                    chips
                    closable-chips
                    class="fund-group-values"
                    :data-testid="`mobile-ui-fundgroup-values-${category}-${groupIndex}`"
                    @update:model-value="setFundGroupValues(group, $event)"
                  />

                  <div class="fund-group-limit">
                    <v-text-field
                      :model-value="group.topN"
                      label="Top N"
                      type="number"
                      min="0"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="fund-group-limit-field"
                      :data-testid="`mobile-ui-fundgroup-topn-${category}-${groupIndex}`"
                      @update:model-value="setFundGroupTopN(group, $event)"
                    />
                    <span class="fund-group-hint fund-group-limit-hint">
                      จำนวนกองทุนสูงสุดที่กลุ่มนี้ส่งให้มือถือ — 0 = ไม่จำกัด (ส่งทุกกองทุนที่ตรงประเภท)
                    </span>
                  </div>

                  <div class="fund-group-sort">
                    <div class="fund-group-sort-title">เรียงกองด้วย (ก่อนตัด Top N) — แถวบนสำคัญกว่าแถวล่าง</div>
                    <div
                      v-for="(sortKey, keyIndex) in group.sortColumns"
                      :key="`${group.rowKey}-sort-${keyIndex}`"
                      class="fund-group-sort-row"
                    >
                      <v-combobox
                        :model-value="sortKey.column"
                        :items="sortableColumns"
                        label="คอลัมน์ของ view"
                        placeholder="เลือกหรือพิมพ์ชื่อคอลัมน์"
                        variant="outlined"
                        density="compact"
                        hide-details
                        hide-no-data
                        class="fund-group-sort-column"
                        :data-testid="`mobile-ui-fundgroup-sortcolumn-${category}-${groupIndex}-${keyIndex}`"
                        @update:model-value="setFundGroupSortColumn(sortKey, $event)"
                      />
                      <v-select
                        v-model="sortKey.descending"
                        :items="sortDirectionItems"
                        label="ทิศทาง"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="fund-group-sort-direction"
                        :data-testid="`mobile-ui-fundgroup-sortdirection-${category}-${groupIndex}-${keyIndex}`"
                        @update:model-value="saveSuccess = ''"
                      />
                      <div class="fund-group-sort-actions">
                        <v-btn
                          variant="text"
                          density="compact"
                          icon
                          :disabled="keyIndex === 0"
                          aria-label="Move sort key up"
                          @click="moveFundGroupSortKey(group, keyIndex, -1)"
                        >
                          <v-icon size="18">mdi-arrow-up</v-icon>
                          <v-tooltip activator="parent" location="top">Move Up</v-tooltip>
                        </v-btn>
                        <v-btn
                          variant="text"
                          density="compact"
                          icon
                          :disabled="keyIndex === group.sortColumns.length - 1"
                          aria-label="Move sort key down"
                          @click="moveFundGroupSortKey(group, keyIndex, 1)"
                        >
                          <v-icon size="18">mdi-arrow-down</v-icon>
                          <v-tooltip activator="parent" location="top">Move Down</v-tooltip>
                        </v-btn>
                        <v-btn
                          variant="text"
                          density="compact"
                          icon
                          color="error"
                          aria-label="Remove sort key"
                          :data-testid="`mobile-ui-fundgroup-sortkey-remove-${category}-${groupIndex}-${keyIndex}`"
                          @click="removeFundGroupSortKey(group, keyIndex, sortKey.column)"
                        >
                          <v-icon size="18">mdi-delete-outline</v-icon>
                          <v-tooltip activator="parent" location="top">Remove</v-tooltip>
                        </v-btn>
                      </div>
                    </div>
                    <v-btn
                      variant="text"
                      density="compact"
                      prepend-icon="mdi-plus"
                      class="fund-group-sort-add"
                      :data-testid="`mobile-ui-fundgroup-sortkey-add-${category}-${groupIndex}`"
                      @click="addFundGroupSortKey(group)"
                    >
                      เพิ่มคอลัมน์เรียง
                    </v-btn>
                  </div>
                  <div class="fund-group-hint">
                    เทียบทีละแถวจากบนลงล่าง แต่ละแถวมีทิศทางของตัวเอง (เหมือน ORDER BY) ตัวเลขเทียบเป็นตัวเลข
                    คอลัมน์ใช่/ไม่ใช่ (เช่น is_recommended) ถือค่าว่างเป็น "ไม่ใช่" ส่วนคอลัมน์อื่นค่าว่างไปท้ายเสมอ —
                    ค่าเริ่มต้น is_recommended DESC, return_1yr DESC = กองแนะนำก่อน แล้วผลตอบแทน 1 ปีมาก→น้อย ·
                    ลบแถว is_recommended = เรียงตามผลตอบแทน 1 ปีล้วน · ไม่มีแถวเลย = ตามลำดับที่ view ส่งมา
                  </div>
                </div>
              </div>
            </section>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-divider />

      <v-card-actions class="mobile-settings-actions">
        <v-btn
          variant="text"
          color="primary"
          prepend-icon="mdi-cached"
          :loading="clearingCache"
          :disabled="saving"
          data-testid="mobile-ui-settings-clear-cache"
          @click="clearCache"
        >
          CLEAR CACHE
          <v-tooltip activator="parent" location="top">
            ล้าง cache ของ GetRecommendedProducts (ปกติหมดอายุเองใน 10 นาที) ให้มือถือดึงข้อมูลใหม่ทันที
          </v-tooltip>
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!canSave"
          data-testid="mobile-ui-settings-save"
          @click="saveSettings"
        >
          SAVE
        </v-btn>
        <v-btn variant="outlined" color="primary" @click="closeDialog">CLOSE</v-btn>
      </v-card-actions>

      <ConfirmDeleteDialog ref="confirmDelete" />
    </v-card>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
import { taxProductDisplayConfigApi } from '../services/taxProductDisplayConfigApi';

// What a fund group is ordered by until the admin changes it — the same default the backend and the column
// script carry (TaxProductDisplayFundGroup.DefaultSortColumns): recommended funds first, then best 1-year return.
// One direction per key, as in a SQL ORDER BY.
const DEFAULT_SORT_COLUMNS = [
  { column: 'is_recommended', descending: true },
  { column: 'return_1yr', descending: true },
];

const CATEGORIES_TAB = '__CATEGORIES__';
const PREFIX_TAB = '__PREFIX__';
const PREFIX_LABEL_MAX_LENGTH = 200;
// The six GetRecommendedProducts prefix fields, grouped the way the phone uses them. The keys are
// the JSON names of both the admin `prefix` object and the mobile payload, so nothing is remapped.
const PREFIX_GROUPS = [
  {
    key: 'product',
    title: 'Product prefix',
    caption: 'คำนำหน้าสินค้า / ประกัน',
    fields: [
      { key: 'prefixProductThai', label: 'In Thai', placeholder: 'ประกัน' },
      { key: 'prefixProductEnglish', label: 'In English', placeholder: 'Insurance' },
      { key: 'prefixProductChinese', label: 'In Chinese', placeholder: '保险' },
    ],
  },
  {
    key: 'fund',
    title: 'Fund prefix',
    caption: 'คำนำหน้ากองทุน',
    fields: [
      { key: 'prefixFundThai', label: 'In Thai', placeholder: 'กองทุน' },
      { key: 'prefixFundEnglish', label: 'In English', placeholder: 'Fund' },
      { key: 'prefixFundChinese', label: 'In Chinese', placeholder: '基金' },
    ],
  },
];
const PREFIX_FIELDS = PREFIX_GROUPS.flatMap((group) => group.fields);
const CATEGORY_CODE_PATTERN = /^[A-Z0-9_]{1,40}$/;
const FUND_VALUE_MAX_LENGTH = 200;

export default {
  name: 'TaxMobileProductSettingsDialog',
  components: { draggable, ConfirmDeleteDialog },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      activeCategory: CATEGORIES_TAB,
      categoryList: [],
      catalog: [],
      // Null until the first successful load: SAVE must not send prefixes at all (let alone
      // blank ones) when the dialog never got the stored rows from the backend.
      prefixValues: null,
      selectedSubtypeCodeByCategory: {},
      rowKeySeed: 0,
      fundGroupKeySeed: 0,
      loading: false,
      saving: false,
      clearingCache: false,
      loadError: '',
      saveError: '',
      saveSuccess: '',
      // Columns of dbo.vw_MutualFund_AllSources, read from the same endpoint the Mutual Fund "View layer" dialog
      // uses, so the chips offer exactly the vocabulary the view has. Empty until loaded (or when the view layer
      // cannot be read) — the combobox still accepts a typed name then.
      sortableColumns: [],
      sortDirectionItems: [
        { title: 'มาก → น้อย (DESC)', value: true },
        { title: 'น้อย → มาก (ASC)', value: false },
      ],
      categoryHeaders: [
        { title: 'Order', key: 'sortOrder', align: 'center', sortable: false, width: '80px' },
        { title: 'Move', key: 'move', align: 'center', sortable: false, width: '76px' },
        { title: 'Code', key: 'categoryCode', align: 'start', sortable: false, width: '160px' },
        { title: 'Label TH', key: 'labelTH', align: 'start', sortable: false, width: '166px' },
        { title: 'Label EN', key: 'labelEN', align: 'start', sortable: false, width: '166px' },
        { title: 'Label CN', key: 'labelCN', align: 'start', sortable: false, width: '166px' },
        { title: 'Active', key: 'isActive', align: 'center', sortable: false, width: '72px' },
        { title: 'Default', key: 'isDefault', align: 'center', sortable: false, width: '72px' },
        { title: '', key: 'actions', align: 'center', sortable: false, width: '60px' },
      ],
    };
  },
  watch: {
    modelValue(value) {
      if (!value) return;
      this.resetState();
      this.loadSettings();
    },
    categories(codes) {
      if (this.activeCategory === CATEGORIES_TAB || this.activeCategory === PREFIX_TAB) return;
      if (!codes.includes(this.activeCategory)) this.activeCategory = CATEGORIES_TAB;
    },
  },
  mounted() {
    if (this.modelValue) {
      this.resetState();
      this.loadSettings();
    }
  },
  computed: {
    dialogModel: {
      get() { return this.modelValue; },
      set(value) { this.$emit('update:modelValue', value); },
    },
    categoriesTab() {
      return CATEGORIES_TAB;
    },
    prefixTab() {
      return PREFIX_TAB;
    },
    prefixGroups() {
      return PREFIX_GROUPS;
    },
    prefixTooLongKeys() {
      if (!this.prefixValues) return [];
      return PREFIX_FIELDS
        .filter((field) => String(this.prefixValues[field.key] || '').length > PREFIX_LABEL_MAX_LENGTH)
        .map((field) => field.key);
    },
    // Same rule the backend applies in NormalizePrefixes, caught before SAVE.
    prefixValidationErrors() {
      if (!this.prefixTooLongKeys.length) return [];
      return [`Prefix must be ${PREFIX_LABEL_MAX_LENGTH} characters or fewer: ${this.prefixTooLongKeys.join(', ')}.`];
    },
    // Tab list: every category that already carries a usable code, in list order.
    categories() {
      const seen = new Set();
      const codes = [];
      this.categoryList.forEach((category) => {
        const code = this.normalizedCode(category.categoryCode);
        if (!code || seen.has(code)) return;
        seen.add(code);
        codes.push(code);
      });
      return codes;
    },
    // Keyed view over the same row objects, so mutations flow straight back to categoryList.
    categorySettings() {
      const settings = {};
      this.categoryList.forEach((category) => {
        const code = this.normalizedCode(category.categoryCode);
        if (!code || settings[code]) return;
        settings[code] = category;
      });
      return settings;
    },
    draggableCategories: {
      get() { return this.categoryList; },
      set(value) { this.categoryList = this.applySortOrder(value); },
    },
    dragDisabled() {
      return this.loading || this.saving || this.categoryList.length < 2;
    },
    invalidCodeRowKeys() {
      const counts = {};
      this.categoryList.forEach((category) => {
        const code = this.normalizedCode(category.categoryCode);
        if (code) counts[code] = (counts[code] || 0) + 1;
      });
      return this.categoryList
        .filter((category) => {
          const code = this.normalizedCode(category.categoryCode);
          return !code || !CATEGORY_CODE_PATTERN.test(code) || counts[code] > 1;
        })
        .map((category) => category.rowKey);
    },
    categoryValidationErrors() {
      const errors = [];
      if (!this.categoryList.length) {
        errors.push('Add at least one category before saving.');
        return errors;
      }

      const counts = {};
      const blankRows = [];
      const malformed = [];
      this.categoryList.forEach((category, index) => {
        const code = this.normalizedCode(category.categoryCode);
        if (!code) {
          blankRows.push(index + 1);
          return;
        }
        if (!CATEGORY_CODE_PATTERN.test(code)) malformed.push(code);
        counts[code] = (counts[code] || 0) + 1;
      });
      if (blankRows.length) errors.push(`Category code is required (row ${blankRows.join(', ')}).`);
      if (malformed.length) {
        errors.push(`Category code must use A–Z, 0–9 or _ only, max 40 characters: ${[...new Set(malformed)].join(', ')}.`);
      }
      const duplicates = Object.keys(counts).filter((code) => counts[code] > 1);
      if (duplicates.length) errors.push(`Duplicate category code: ${duplicates.join(', ')}.`);

      if (!this.categoryList.some((category) => category.isActive)) {
        errors.push('At least one category must stay active.');
      }

      const defaults = this.categoryList.filter((category) => category.isDefault);
      if (!defaults.length) {
        errors.push('Choose a default category.');
      } else if (!defaults.some((category) => category.isActive)) {
        errors.push('The default category must be active.');
      }

      return errors;
    },
    // Fund-type values are matched literally against the source data, so the same value
    // must never live in two groups — it would list the very same fund twice on mobile.
    fundGroupValidationErrors() {
      const errors = [];
      const emptyGroups = [];
      const blankValues = [];
      const longValues = [];
      const localDuplicates = [];
      const crossDuplicates = [];
      const owners = new Map();

      this.categoryList.forEach((category) => {
        const categoryCode = this.normalizedCode(category.categoryCode) || 'NEW CATEGORY';
        const groups = Array.isArray(category.fundGroups) ? category.fundGroups : [];

        groups.forEach((group, index) => {
          const where = `${categoryCode} › ${this.fundGroupTitle(group, index)}`;
          const values = (Array.isArray(group.values) ? group.values : []).map((value) => String(value ?? '').trim());

          if (!values.some(Boolean)) emptyGroups.push(where);

          const seenHere = new Set();
          values.forEach((value) => {
            if (!value) {
              blankValues.push(where);
              return;
            }
            if (value.length > FUND_VALUE_MAX_LENGTH) longValues.push(where);

            const key = value.toLowerCase();
            if (seenHere.has(key)) {
              localDuplicates.push(`"${value}" in ${where}`);
              return;
            }
            seenHere.add(key);

            const owner = owners.get(key);
            if (owner) crossDuplicates.push(`"${value}" is in both ${owner} and ${where}`);
            else owners.set(key, where);
          });
        });
      });

      const unique = (list) => [...new Set(list)];
      if (emptyGroups.length) {
        errors.push(`Add at least one fund type to: ${unique(emptyGroups).join(', ')}.`);
      }
      if (blankValues.length) {
        errors.push(`Fund type cannot be blank: ${unique(blankValues).join(', ')}.`);
      }
      if (longValues.length) {
        errors.push(`Fund type must be ${FUND_VALUE_MAX_LENGTH} characters or fewer: ${unique(longValues).join(', ')}.`);
      }
      if (localDuplicates.length) {
        errors.push(`Duplicate fund type: ${unique(localDuplicates).join(', ')}.`);
      }
      if (crossDuplicates.length) {
        errors.push(`A fund type can only belong to one group — ${unique(crossDuplicates).join(', ')}.`);
      }

      return errors;
    },
    validationErrors() {
      return [
        ...this.categoryValidationErrors,
        ...this.fundGroupValidationErrors,
        ...this.prefixValidationErrors,
      ];
    },
    validationMessage() {
      if (this.loading) return '';
      return this.validationErrors.join(' · ');
    },
    hasConfigurableCategories() {
      return this.categoryList.length > 0;
    },
    canSave() {
      return !this.loading
        && !this.saving
        && !this.loadError
        && this.hasConfigurableCategories
        && this.validationErrors.length === 0;
    },
  },
  methods: {
    resetState() {
      this.categoryList = [];
      this.catalog = [];
      this.prefixValues = null;
      this.selectedSubtypeCodeByCategory = {};
      this.activeCategory = CATEGORIES_TAB;
      this.rowKeySeed = 0;
      this.fundGroupKeySeed = 0;
      this.loadError = '';
      this.saveError = '';
      this.saveSuccess = '';
      this.saving = false;
      this.clearingCache = false;
    },
    async loadSettings() {
      this.loading = true;
      this.loadError = '';
      this.saveError = '';
      this.saveSuccess = '';
      this.loadSortableColumns();
      try {
        const result = await taxProductDisplayConfigApi.getConfig();
        this.applyConfig(result);
      } catch (error) {
        this.categoryList = [];
        this.catalog = [];
        this.prefixValues = null;
        this.selectedSubtypeCodeByCategory = {};
        this.activeCategory = CATEGORIES_TAB;
        this.loadError = error?.message || 'Unable to load product display settings.';
      } finally {
        this.loading = false;
      }
    },
    applyConfig(result) {
      const responseCategories = Array.isArray(result?.categories) ? result.categories : [];
      const nextCatalog = (Array.isArray(result?.catalog) ? result.catalog : []).map(this.toSubcategory);

      const seen = new Set();
      const nextList = [];
      responseCategories.forEach((category) => {
        const categoryCode = this.normalizedCode(category?.categoryCode);
        if (!categoryCode || seen.has(categoryCode)) return;
        seen.add(categoryCode);

        const items = Array.isArray(category.items) ? category.items : [];
        const subcategories = items.length
          ? items.map(this.toSubcategory)
          : nextCatalog.map((item) => ({ ...item }));
        const selectedCodes = subcategories.filter((item) => item.isVisible).map((item) => item.code);

        nextList.push({
          rowKey: this.nextRowKey(),
          categoryCode,
          labelTH: category.labelTH || '',
          labelEN: category.labelEN || '',
          labelCN: category.labelCN || '',
          sortOrder: Number(category.sortOrder) || nextList.length + 1,
          isActive: category.isActive !== false,
          isDefault: Boolean(category.isDefault),
          selectionLimit: Number(category.selectionLimit) || 0,
          isPersisted: true,
          selectedCodes,
          subcategories,
          fundGroups: this.toFundGroups(category.fundGroups),
        });
      });

      nextList.sort((left, right) => left.sortOrder - right.sortOrder);

      const nextSelectedRows = {};
      nextList.forEach((category) => {
        nextSelectedRows[category.categoryCode] = category.selectedCodes[0] || null;
      });

      const previousActive = this.activeCategory;
      const nextCodes = nextList.map((category) => category.categoryCode);

      this.catalog = nextCatalog;
      this.categoryList = this.applySortOrder(nextList);
      this.selectedSubtypeCodeByCategory = nextSelectedRows;
      this.prefixValues = this.toPrefixValues(result?.prefix);

      // The two fixed tabs survive a reload as they are; a category tab only if its code still exists.
      const fixedTabs = [CATEGORIES_TAB, PREFIX_TAB];
      if (!fixedTabs.includes(previousActive) && !nextCodes.includes(previousActive)) {
        this.activeCategory = nextCodes[0] || CATEGORIES_TAB;
      }
    },
    toSubcategory(item) {
      return {
        subtypeId: item.subtypeId ?? item.id,
        code: item.code || String(item.subtypeId ?? item.id),
        productType: item.productType || '',
        name: item.name || '',
        order: item.displayOrder || 0,
        labelEN: item.labelEN || item.name || '',
        labelTH: item.labelTH || '',
        labelCN: item.labelCN || '',
        groupCode: String(item.groupCode || '').trim(),
        isVisible: Boolean(item.isVisible),
      };
    },
    // The six boxes, keyed by their JSON name. An older backend answers without `prefix`; keep
    // null so buildPayload leaves it out instead of blanking a row that backend may not even have.
    toPrefixValues(prefix) {
      if (!prefix || typeof prefix !== 'object') return null;

      const values = {};
      PREFIX_FIELDS.forEach((field) => { values[field.key] = String(prefix[field.key] || ''); });
      return values;
    },
    // Not trimmed on purpose: a trailing space is how the admin puts a gap between the prefix
    // and the item title the phone glues it to. The backend keeps whitespace as typed too.
    prefixPayload() {
      const prefix = {};
      PREFIX_FIELDS.forEach((field) => { prefix[field.key] = String(this.prefixValues[field.key] || ''); });
      return prefix;
    },
    toFundGroups(groups) {
      const rows = (Array.isArray(groups) ? groups : []).map((group, index) => ({
        rowKey: this.nextFundGroupKey(),
        groupCode: String(group?.groupCode || '').trim(),
        labelTH: group?.labelTH || '',
        labelEN: group?.labelEN || '',
        labelCN: group?.labelCN || '',
        past1YearReturnLabelTH: group?.past1YearReturnLabelTH || '',
        past1YearReturnLabelEN: group?.past1YearReturnLabelEN || '',
        past1YearReturnLabelCN: group?.past1YearReturnLabelCN || '',
        sortOrder: Number(group?.sortOrder) || index + 1,
        topN: this.normalizedTopN(group?.topN),
        // The backend always sends the list (the shipped default for a group saved before the field existed),
        // so a missing key here is a response from an older backend: keep that default rather than "no order".
        sortColumns: Array.isArray(group?.sortColumns)
          ? this.normalizedSortKeys(group.sortColumns)
          : DEFAULT_SORT_COLUMNS.map((key) => ({ ...key })),
        isActive: group?.isActive !== false,
        values: (Array.isArray(group?.values) ? group.values : [])
          .map((value) => String(value ?? '').trim())
          .filter(Boolean),
      }));
      rows.sort((left, right) => left.sortOrder - right.sortOrder);
      return this.applySortOrder(rows);
    },
    buildPayload() {
      return {
        // Omitted when the boxes were never loaded: the backend reads a missing `prefix` as
        // "leave the stored prefixes alone".
        ...(this.prefixValues ? { prefix: this.prefixPayload() } : {}),
        categories: this.categoryList.map((category, index) => ({
          categoryCode: this.normalizedCode(category.categoryCode),
          labelTH: String(category.labelTH || '').trim(),
          labelEN: String(category.labelEN || '').trim(),
          labelCN: String(category.labelCN || '').trim(),
          sortOrder: index + 1,
          isActive: Boolean(category.isActive),
          isDefault: Boolean(category.isDefault),
          fundGroups: (Array.isArray(category.fundGroups) ? category.fundGroups : []).map((group, groupIndex) => ({
            groupCode: String(group.groupCode || '').trim(),
            labelTH: String(group.labelTH || '').trim(),
            labelEN: String(group.labelEN || '').trim(),
            labelCN: String(group.labelCN || '').trim(),
            past1YearReturnLabelTH: String(group.past1YearReturnLabelTH || '').trim(),
            past1YearReturnLabelEN: String(group.past1YearReturnLabelEN || '').trim(),
            past1YearReturnLabelCN: String(group.past1YearReturnLabelCN || '').trim(),
            sortOrder: groupIndex + 1,
            topN: this.normalizedTopN(group.topN),
            sortColumns: this.normalizedSortKeys(group.sortColumns),
            isActive: Boolean(group.isActive),
            values: (Array.isArray(group.values) ? group.values : [])
              .map((value) => String(value ?? '').trim())
              .filter(Boolean),
          })),
          items: category.subcategories
            .filter((subcategory) => category.selectedCodes.includes(subcategory.code))
            .map((subcategory, itemIndex) => ({
              subtypeId: subcategory.subtypeId,
              isVisible: true,
              displayOrder: itemIndex + 1,
              labelEN: subcategory.labelEN || '',
              labelTH: subcategory.labelTH || '',
              labelCN: subcategory.labelCN || '',
              groupCode: this.normalizedCode(subcategory.groupCode),
            })),
        })),
      };
    },
    async saveSettings() {
      if (!this.canSave) return;

      this.saving = true;
      this.saveError = '';
      this.saveSuccess = '';
      try {
        const result = await taxProductDisplayConfigApi.updateConfig(this.buildPayload());
        this.applyConfig(result);
        this.saveSuccess = 'Saved successfully.';
      } catch (error) {
        this.saveError = error?.message || 'Unable to save product display settings.';
      } finally {
        this.saving = false;
      }
    },
    // The payload behind GetRecommendedProducts is cached server-side for ten minutes, and a SAVE here
    // already invalidates it. This is for a change made somewhere else — a fund row edited straight in the
    // database — that the mobile screen would otherwise keep missing until the ten minutes ran out.
    async clearCache() {
      this.clearingCache = true;
      this.saveError = '';
      this.saveSuccess = '';
      try {
        await taxProductDisplayConfigApi.clearRecommendedProductsCache();
        this.saveSuccess = 'Recommended products cache cleared. The next mobile request rebuilds it.';
      } catch (error) {
        this.saveError = error?.message || 'Unable to clear the recommended products cache.';
      } finally {
        this.clearingCache = false;
      }
    },
    closeDialog() { this.dialogModel = false; },

    nextRowKey() {
      this.rowKeySeed += 1;
      return `category-${this.rowKeySeed}`;
    },
    nextFundGroupKey() {
      this.fundGroupKeySeed += 1;
      return `fund-group-${this.fundGroupKeySeed}`;
    },
    setPrefixValue(key, value) {
      if (!this.prefixValues) return;
      this.prefixValues[key] = String(value ?? '');
      this.saveSuccess = '';
    },
    normalizedCode(value) {
      return String(value || '').trim().toUpperCase();
    },
    categoryRowId(category, index) {
      return this.normalizedCode(category?.categoryCode) || `new-${index + 1}`;
    },
    applySortOrder(items) {
      items.forEach((item, index) => { item.sortOrder = index + 1; });
      return items;
    },
    moveCategory(fromIndex, direction) {
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= this.categoryList.length) return;

      const items = [...this.categoryList];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      this.categoryList = this.applySortOrder(items);
    },
    addCategory() {
      this.categoryList.push({
        rowKey: this.nextRowKey(),
        categoryCode: '',
        labelTH: '',
        labelEN: '',
        labelCN: '',
        sortOrder: this.categoryList.length + 1,
        isActive: true,
        isDefault: false,
        selectionLimit: 0,
        isPersisted: false,
        selectedCodes: [],
        subcategories: this.catalog.map((item) => ({ ...item, isVisible: false })),
        fundGroups: [],
      });
      this.saveSuccess = '';
    },
    async removeCategory(index, label) {
      if (!(await this.$refs.confirmDelete.ask({ message: 'ต้องการลบหมวดหมู่นี้ใช่หรือไม่?', target: label, note: 'การลบจะมีผลเมื่อกด SAVE' }))) return;
      const [removed] = this.categoryList.splice(index, 1);
      if (!removed) return;

      const removedCode = this.normalizedCode(removed.categoryCode);
      if (removedCode) delete this.selectedSubtypeCodeByCategory[removedCode];
      if (removedCode && this.activeCategory === removedCode) this.activeCategory = CATEGORIES_TAB;

      if (removed.isDefault) {
        const fallback = this.categoryList.find((category) => category.isActive) || this.categoryList[0];
        if (fallback) fallback.isDefault = true;
      }

      this.applySortOrder(this.categoryList);
      this.saveSuccess = '';
    },
    setCategoryCode(category, value) {
      const previousCode = this.normalizedCode(category.categoryCode);
      const nextCode = String(value || '').toUpperCase().replace(/\s+/g, '');
      category.categoryCode = nextCode;

      if (previousCode && previousCode !== nextCode) {
        const selected = this.selectedSubtypeCodeByCategory[previousCode];
        delete this.selectedSubtypeCodeByCategory[previousCode];
        if (nextCode) this.selectedSubtypeCodeByCategory[nextCode] = selected || null;
      }
      this.saveSuccess = '';
    },
    // Driven by the radio's click event, not update:modelValue. VRadio declares a modelValue prop
    // but leaves that emit to the radio group it expects to sit in, so Vue strips the listener from
    // the fallthrough attrs of a standalone radio: it never fires. Bound that way the handler never
    // ran — the clicked row lit itself up from its own internal state, the previous default stayed
    // lit, and SAVE still carried the old default.
    setDefaultCategory(category) {
      if (!category) return;
      // A click on the row that is already the only default changes nothing, so leave the
      // success banner alone: nothing was edited.
      const changed = this.categoryList.some((row) => row.isDefault !== (row === category));
      if (!changed) return;

      this.categoryList.forEach((row) => { row.isDefault = row === category; });
      this.saveSuccess = '';
    },

    categoryTitleFor(category) {
      const settings = this.categorySettings[category];
      if (!settings) return 'Subcategory catalog';
      const label = String(settings.labelEN || settings.labelTH || settings.categoryCode || '').trim();
      return label ? `${label} subcategory catalog` : 'Subcategory catalog';
    },
    subcategoriesFor(category) {
      return this.categorySettings[category]?.subcategories || [];
    },
    selectedCountFor(category) {
      return this.categorySettings[category]?.selectedCodes.length || 0;
    },
    selectedSubtypeFor(category) {
      const selectedCode = this.selectedSubtypeCodeByCategory[category];
      return this.categorySettings[category]?.subcategories.find((subcategory) => subcategory.code === selectedCode) || null;
    },
    isSubtypeSelected(category, code) {
      return this.categorySettings[category]?.selectedCodes.includes(code) || false;
    },
    selectSubtype(category, code) {
      this.selectedSubtypeCodeByCategory[category] = code;
    },
    setSubtypeEnabled(category, code, enabled) {
      const config = this.categorySettings[category];
      if (!config) return;

      const currentCodes = config.selectedCodes;
      if (enabled) {
        if (!currentCodes.includes(code)) {
          config.selectedCodes = [...currentCodes, code];
          this.selectedSubtypeCodeByCategory[category] = code;
          // Switched back on with a code another selected row already carries: it rejoins that group.
          const subcategory = config.subcategories.find((row) => row.code === code);
          if (subcategory) this.adoptGroupLabels(category, subcategory);
        }
        return;
      }

      config.selectedCodes = currentCodes.filter((selectedCode) => selectedCode !== code);
      if (this.selectedSubtypeCodeByCategory[category] === code) {
        this.selectedSubtypeCodeByCategory[category] = config.selectedCodes[0] || null;
      }
    },
    updateLabel(category, field, value) {
      const subtype = this.selectedSubtypeFor(category);
      if (!subtype) return;
      subtype[field] = value;
      // The labels belong to the group, not the row: the phone prints one header and one chip per
      // group code, so every selected row sharing the code shows — and saves — the same text.
      this.groupMatesFor(category, subtype).forEach((mate) => { mate[field] = value; });
    },
    updateGroupCode(category, value) {
      // Uppercased as it is typed, the way category and fund group codes already are:
      // the backend stores it uppercase, so anything else would look edited after a save.
      const subtype = this.selectedSubtypeFor(category);
      if (!subtype) return;
      subtype.groupCode = this.normalizedCode(value);
      this.adoptGroupLabels(category, subtype);
    },
    // The other selected rows of this tab carrying the same group code. On the phone they and this
    // row are ONE group (one header, one chip, one card list), which is why they share the labels.
    groupMatesFor(category, subcategory) {
      const code = this.normalizedCode(subcategory?.groupCode);
      if (!code) return [];
      return this.subcategoriesFor(category).filter((row) =>
        row !== subcategory
        && this.isSubtypeSelected(category, row.code)
        && this.normalizedCode(row.groupCode) === code);
    },
    groupMateNames(category, subcategory) {
      return this.groupMatesFor(category, subcategory).map((row) => row.name || row.code).join(', ');
    },
    // A row that joins an existing group takes the group's labels: the group already has its header
    // on the phone, and the backend stores the first row's labels on every row of the group anyway.
    adoptGroupLabels(category, subcategory) {
      const owner = this.groupMatesFor(category, subcategory)[0];
      if (!owner) return;
      ['labelEN', 'labelTH', 'labelCN'].forEach((field) => { subcategory[field] = owner[field] || ''; });
    },

    fundGroupsFor(category) {
      return this.categorySettings[category]?.fundGroups || [];
    },
    fundGroupTitle(group, index) {
      const label = String(group?.labelEN || group?.labelTH || group?.labelCN || '').trim();
      return label || `Fund group ${index + 1}`;
    },
    addFundGroup(category) {
      const config = this.categorySettings[category];
      if (!config) return;

      if (!Array.isArray(config.fundGroups)) config.fundGroups = [];
      config.fundGroups.push({
        rowKey: this.nextFundGroupKey(),
        groupCode: '',
        labelTH: '',
        labelEN: '',
        labelCN: '',
        past1YearReturnLabelTH: '',
        past1YearReturnLabelEN: '',
        past1YearReturnLabelCN: '',
        sortOrder: config.fundGroups.length + 1,
        topN: 0,
        sortColumns: DEFAULT_SORT_COLUMNS.map((key) => ({ ...key })),
        isActive: true,
        values: [],
      });
      this.saveSuccess = '';
    },
    async removeFundGroup(category, index, label) {
      if (!(await this.$refs.confirmDelete.ask({ message: 'ต้องการลบกลุ่มกองทุนนี้ใช่หรือไม่?', target: label, note: 'การลบจะมีผลเมื่อกด SAVE' }))) return;
      const config = this.categorySettings[category];
      if (!config || !Array.isArray(config.fundGroups)) return;

      config.fundGroups.splice(index, 1);
      this.applySortOrder(config.fundGroups);
      this.saveSuccess = '';
    },
    // Reorder stays in memory — nothing is written until SAVE.
    moveFundGroup(category, fromIndex, direction) {
      const config = this.categorySettings[category];
      if (!config || !Array.isArray(config.fundGroups)) return;

      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= config.fundGroups.length) return;

      const groups = [...config.fundGroups];
      const [moved] = groups.splice(fromIndex, 1);
      groups.splice(toIndex, 0, moved);
      config.fundGroups = this.applySortOrder(groups);
      this.saveSuccess = '';
    },
    setFundGroupValues(group, values) {
      group.values = (Array.isArray(values) ? values : []).map((value) => String(value ?? ''));
      this.saveSuccess = '';
    },
    setFundGroupSortColumn(sortKey, value) {
      sortKey.column = String(value ?? '').trim();
      this.saveSuccess = '';
    },
    addFundGroupSortKey(group) {
      if (!Array.isArray(group.sortColumns)) group.sortColumns = [];
      group.sortColumns.push({ column: '', descending: true });
      this.saveSuccess = '';
    },
    async removeFundGroupSortKey(group, index, label) {
      if (!(await this.$refs.confirmDelete.ask({ message: 'ต้องการลบคอลัมน์เรียงลำดับนี้ใช่หรือไม่?', target: label, note: 'การลบจะมีผลเมื่อกด SAVE' }))) return;
      if (!Array.isArray(group.sortColumns)) return;
      group.sortColumns.splice(index, 1);
      this.saveSuccess = '';
    },
    moveFundGroupSortKey(group, index, delta) {
      const keys = group.sortColumns;
      const target = index + delta;
      if (!Array.isArray(keys) || target < 0 || target >= keys.length) return;
      [keys[index], keys[target]] = [keys[target], keys[index]];
      this.saveSuccess = '';
    },
    // Rows in the order the admin arranged them, one direction each; blank columns are dropped and a column
    // named twice keeps its first row — the backend rejects anything that is not a plain column name, so a
    // typo surfaces on SAVE with the field named.
    normalizedSortKeys(values) {
      const seen = new Set();
      return (Array.isArray(values) ? values : [])
        .map((key) => ({
          column: String(key?.column ?? '').trim(),
          descending: key?.descending !== false,
        }))
        .filter((key) => {
          if (!key.column) return false;
          const name = key.column.toLowerCase();
          if (seen.has(name)) return false;
          seen.add(name);
          return true;
        });
    },
    loadSortableColumns() {
      const call = this.$call;
      if (!call || typeof call.GetMutualFundViewLayer !== 'function') return;
      call.GetMutualFundViewLayer(
        (response) => {
          // The $call mixin hands back the ResponseModel itself — status 0 is success, the payload is `data`.
          if (!response || response.status !== 0) return;
          const sources = Array.isArray(response.data?.sources) ? response.data.sources : [];
          const columns = new Set();
          sources.forEach((source) => {
            (Array.isArray(source?.columns) ? source.columns : []).forEach((column) => {
              const name = String(column ?? '').trim();
              if (name) columns.add(name);
            });
          });
          this.sortableColumns = [...columns].sort((left, right) => left.localeCompare(right));
        },
        () => {
          // Not an error the admin has to read: the chips simply offer nothing to pick from and accept typing.
          this.sortableColumns = [];
        },
      );
    },
    // The number field hands back a string, and clearing it hands back an empty one. An
    // emptied box has to stay empty while the admin types the next number, so only the
    // payload turns it into the zero the backend reads as "no cap".
    setFundGroupTopN(group, value) {
      const text = String(value ?? '').trim();
      group.topN = text === '' ? '' : this.normalizedTopN(text);
      this.saveSuccess = '';
    },
    normalizedTopN(value) {
      const parsed = Math.trunc(Number(value));
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
  },
};
</script>

<style scoped>
.mobile-settings-card { overflow: hidden; border-radius: 18px !important; }
.mobile-settings-header { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 24px 28px 20px; background: linear-gradient(135deg, #f4f2fb 0%, #eef5ff 100%); }
.mobile-settings-heading { display: flex; align-items: center; gap: 14px; min-width: 0; }
.mobile-settings-icon { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 48px; height: 48px; border-radius: 14px; color: #4e3982; background: rgba(78, 57, 130, 0.12); }
.mobile-settings-title { color: #2b2450; font-size: 1.22rem; font-weight: 700; line-height: 1.35; }
.mobile-settings-subtitle, .section-description { color: #6b7280; font-size: 0.83rem; line-height: 1.5; }
.mobile-settings-tabs { flex: none; min-height: 48px; padding: 0 22px; background: #fff; }
.mobile-settings-body { min-height: 0; padding: 28px; background: #fbfbfd; }
.category-settings-layout { display: grid; grid-template-columns: minmax(360px, 1fr) minmax(320px, 1fr); gap: 12px; min-height: 460px; }
.section-heading-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.section-title { color: #25213d; font-size: 1rem; font-weight: 700; }
.category-manager { min-height: 460px; }
.category-table-wrap { overflow-x: auto; border: 1px solid #e3e1e9; border-radius: 12px; background: #fff; }
.category-data-table { min-width: 1018px; box-shadow: none !important; }
.prefix-manager { max-width: 860px; }
/* One card per row, stacked — never side by side. */
.prefix-card-list { grid-template-columns: minmax(0, 1fr); }
.prefix-card-caption { color: #777287; font-size: 0.78rem; }
.prefix-field { margin-bottom: 16px; }
.prefix-field:last-child { margin-bottom: 0; }
.prefix-field-label { display: block; margin-bottom: 6px; color: #25213d; font-size: 0.86rem; font-weight: 500; }
.prefix-field-hint { margin: 4px 0 0 14px; color: #9a95a8; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.7rem; letter-spacing: 0.02em; }
.category-row td { vertical-align: middle; }
.category-row--off { background: #fafafc; }
.category-empty-cell { padding: 28px 14px; color: #8b8798; text-align: center; }
.order-cell, .move-cell { display: flex; align-items: center; justify-content: center; gap: 4px; }
.drag-handle { cursor: grab; color: #757575; }
.drag-handle:active { cursor: grabbing; }
.drag-ghost { background: rgba(78, 57, 130, 0.08) !important; opacity: 0.55; }
.drag-chosen { background: rgba(78, 57, 130, 0.06) !important; }
.drag-active { box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16); }
.subtype-table-wrap { max-height: 465px; overflow-y: auto; scrollbar-gutter: stable; scrollbar-color: #c3bdcf #f7f6fa; scrollbar-width: thin; border: 1px solid #e3e1e9; border-radius: 12px; background: #fff; }
.subtype-table-wrap::-webkit-scrollbar { width: 6px; }
.subtype-table-wrap::-webkit-scrollbar-track { background: #f7f6fa; }
.subtype-table-wrap::-webkit-scrollbar-thumb { min-height: 28px; border: 1px solid #f7f6fa; border-radius: 999px; background: #c3bdcf; }
.subtype-table-wrap::-webkit-scrollbar-thumb:hover { background: #aaa1bb; }
.subtype-table { width: 100%; min-width: 360px; border-collapse: collapse; color: #343044; font-size: 0.84rem; }
.subtype-table th { position: sticky; top: 0; z-index: 1; padding: 13px 14px; border-bottom: 1px solid #dedce5; background: #f6f5f9; color: #5b566b; font-size: 0.76rem; font-weight: 700; text-align: left; white-space: nowrap; }
.subtype-table td { height: 58px; padding: 9px 14px; border-bottom: 1px solid #eceaf0; vertical-align: middle; }
.subtype-table tbody tr { cursor: pointer; outline: none; transition: background-color 160ms ease, box-shadow 160ms ease; }
.subtype-table tbody tr:hover, .subtype-table tbody tr:focus-visible { background: #faf9fe; box-shadow: inset 3px 0 0 rgba(78, 57, 130, 0.38); }
.subtype-table tbody tr.subtype-row--active { background: #f3f0fc; box-shadow: inset 3px 0 0 #4e3982; }
.subtype-table tbody tr.subtype-row--empty { cursor: default; color: #8b8798; text-align: center; }
.subtype-table tbody tr.subtype-row--empty:hover { background: transparent; box-shadow: none; }
.subtype-table tbody tr:last-child td { border-bottom: 0; }
.subtype-type { display: block; color: #777287; font-size: 0.78rem; font-weight: 600; }
.subtype-name { display: block; color: #343044; font-weight: 600; }
.subtype-code { display: block; margin-top: 2px; color: #777287; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.72rem; letter-spacing: 0.04em; }
.subtype-code-icon { margin-right: 4px; vertical-align: -1px; color: #4e3982; }
.subtype-table-action-heading, .subtype-table-action-cell { text-align: center !important; }
.subtype-toggle { position: relative; display: inline-flex; align-items: center; justify-content: center; min-width: 44px; min-height: 44px; cursor: pointer; }
.subtype-toggle input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.subtype-toggle-track { display: inline-flex; align-items: center; width: 38px; height: 22px; padding: 3px; border-radius: 999px; background: #c8c6d0; transition: background 160ms ease; }
.subtype-toggle-thumb { width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2); transition: transform 160ms ease; }
.subtype-toggle input:checked + .subtype-toggle-track { background: #4e3982; }
.subtype-toggle input:checked + .subtype-toggle-track .subtype-toggle-thumb { transform: translateX(16px); }
.subtype-toggle input:focus-visible + .subtype-toggle-track { outline: 3px solid rgba(78, 57, 130, 0.24); outline-offset: 2px; }
.subtype-toggle input:disabled + .subtype-toggle-track { cursor: not-allowed; opacity: 0.45; }
.subtype-editor { padding: 4px 0 0; }
.label-fields { display: grid; gap: 16px; }
.fund-group-manager { margin-top: 26px; padding-top: 22px; border-top: 1px solid #e3e1e9; }
.fund-group-list { display: grid; gap: 14px; }
.fund-group-card { padding: 16px 18px 18px; border: 1px solid #e3e1e9; border-radius: 12px; background: #fff; }
.fund-group-card--off { background: #fafafc; }
.fund-group-card-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.fund-group-card-name { min-width: 0; overflow: hidden; color: #25213d; font-size: 0.9rem; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.fund-group-card-actions { display: flex; align-items: center; gap: 2px; margin-left: auto; }
.fund-group-active { flex: 0 0 auto; margin-right: 6px; }
.fund-group-active :deep(.v-label) { color: #6b7280; font-size: 0.8rem; opacity: 1; }
.fund-group-label-fields { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.fund-group-hint { margin: 8px 0 14px; color: #6b7280; font-size: 0.78rem; line-height: 1.5; }
.fund-group-limit { display: flex; align-items: center; gap: 12px; margin-top: 14px; }
.fund-group-limit-field { flex: 0 0 140px; max-width: 140px; }
.fund-group-limit-hint { margin: 0; }
.fund-group-sort { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.fund-group-sort-title { font-size: 0.82rem; font-weight: 500; color: #4b5563; }
.fund-group-sort-row { display: flex; align-items: center; gap: 8px; }
.fund-group-sort-column { flex: 1 1 auto; min-width: 0; }
.fund-group-sort-direction { flex: 0 0 190px; max-width: 190px; }
.fund-group-sort-actions { display: flex; align-items: center; flex: 0 0 auto; }
.fund-group-sort-add { align-self: flex-start; }
.fund-group-empty { padding: 28px 14px; border: 1px dashed #d8d4e7; border-radius: 12px; background: #fff; color: #8b8798; text-align: center; }
.category-placeholder { display: flex; min-height: 460px; flex-direction: column; align-items: center; justify-content: center; gap: 12px; border: 1px dashed #d8d4e7; border-radius: 16px; background: #fff; text-align: center; }
.mobile-settings-actions { min-height: 68px; padding: 14px 28px; background: #fff; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 1000px) {
  .category-settings-layout { grid-template-columns: 1fr; gap: 22px; }
  .subtype-table-wrap { max-height: 340px; }
  .fund-group-label-fields { grid-template-columns: 1fr; }
  .fund-group-limit { flex-wrap: wrap; }
  .fund-group-sort-row { flex-wrap: wrap; }
  .fund-group-sort-direction { flex-basis: 100%; max-width: none; }
}
@media (max-width: 600px) {
  .mobile-settings-header, .mobile-settings-body, .mobile-settings-actions { padding-left: 18px; padding-right: 18px; }
  .mobile-settings-title { font-size: 1.05rem; }
  .mobile-settings-subtitle { font-size: 0.76rem; }
}
@media (prefers-reduced-motion: reduce) {
  .subtype-table tbody tr, .subtype-toggle-track, .subtype-toggle-thumb { transition: none; }
}
</style>
